import { Link } from "react-router-dom";
import signalLogo from "@/assets/signal-logo-on-dark.svg";
import signalLogoOnLight from "@/assets/signal-logo-on-light.svg";
import { useSupportChat } from "@/components/SupportChatProvider";
import { EXTERNAL_LINK_PROPS, PRIVACY_POLICY_URL } from "@/lib/legal";
import { TOUCH_TARGET } from "@/components/buttonStyles";

// Shared footer bar: the signal lockup + legal identity on the left and the
// legal/support links (divider-separated) on the right. Sized + padded to match
// the header bar. Used by both the marketing shell (AppShell) and the
// authenticated shell (PortalShell) so the identity block, the `build-info`
// marker, and the links live in one place (DRY). The version is inlined at build
// time via __APP_VERSION__. Contact Support opens live chat when it is
// configured (BRK-S136) and is left inert when it isn't. The portal leaves it
// out (`contactSupport={false}`): its sidebar's Support is the one entry there
// (BRK-S258). Signed out, the footer's link is the only way to reach us.
//
// In the authenticated shell it is `sticky` (BRK-S228): from lg up it stays at
// the bottom of the viewport, at the fixed `--brk-footer-h` the sidebar is sized
// against. Below lg it stays at the end of the page, leaving the bottom of a
// phone to the form action dock (BRK-S198). The marketing shell's footer is
// not sticky.
//
// One line in caption from md (BRK-S262; Nav, 2026-09-28: "The footer has also
// more height"): the logo at 20px, then the © line, the address and the build,
// with the links on the right. It was two lines in 77px; it is 40px at lg+.
// The line needs about 910px with the address. Where the footer is narrower
// (beside the open sidebar at 1024, say), the address drops so the rest stays
// on one line: a container query in src/index.css, since the room depends on
// the sidebar as well as the viewport. Below md the footer stacks.
//
// Its surface follows the theme in both shells (BRK-S247, BRK-S249): white by
// day and Ink by night, swapping the lockup rather than recolouring it (brand
// book). The address and build number stay at dim, the palette's secondary
// type, which test/lib/themeContrast.test.ts holds to WCAG AA in both schemes.
//
// The legal links (BRK-S149) are the reason the footer is the right home for
// them: it is the only chrome both shells share, so /terms is reachable from
// every screen, signed out and signed in. A link is in-app (`to`) or off-origin
// (`href`); one with neither is still presentational, and — unlike the nav rows
// — deliberately does NOT fall through to live chat (BRK-S136).
const LINKS: Array<{ label: string; to?: string; href?: string }> = [
  // ONE privacy policy for the whole entity, on the marketing site. Linked,
  // never restated here — see src/lib/legal.ts.
  { label: "Privacy Policy", href: PRIVACY_POLICY_URL },
  { label: "Terms of Service", to: "/terms" },
  { label: "Security" },
];

const LINK_CLASS = `transition-colors hover:text-brk-text ${TOUCH_TARGET}`;
/** Tighter below sm, so the row fits a 320px phone (BRK-S248). */
const DIVIDER_CLASS = "mx-1.5 h-3 w-px bg-brk-border sm:mx-3";

export function SiteFooter({
  sticky = false,
  contactSupport = true,
}: {
  sticky?: boolean;
  contactSupport?: boolean;
}) {
  const year = new Date().getFullYear();
  const { enabled: chatEnabled, open: openChat } = useSupportChat();

  return (
    <footer
      data-site-footer
      className={`border-t border-brk-border bg-brk-panel text-caption text-brk-dim ${
        sticky ? "lg:sticky lg:bottom-0 lg:z-30 lg:h-[var(--brk-footer-h)]" : ""
      }`}
    >
      <div
        className={`flex flex-col gap-3 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between md:gap-6 md:py-3 ${
          sticky ? "lg:h-full lg:py-0" : ""
        }`}
      >
        {/* Left: the logo and the legal identity, one line from md. */}
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={signalLogoOnLight}
            alt=""
            className="h-5 w-auto shrink-0 dark:hidden"
          />
          <img
            src={signalLogo}
            alt=""
            className="hidden h-5 w-auto shrink-0 dark:block"
          />
          <p className="flex min-w-0 flex-wrap items-center gap-x-2">
            <span className="md:whitespace-nowrap">
              © {year} Signal Specialty Insurance Services, LLC
            </span>
            <span data-footer-address className="flex items-center gap-x-2">
              <span aria-hidden="true" className="hidden md:inline">
                ·
              </span>
              <span className="md:whitespace-nowrap">
                25 Bridge St, Red Bank NJ, 07701
              </span>
            </span>
            <span className="flex items-center gap-x-2">
              <span aria-hidden="true" className="hidden md:inline">
                ·
              </span>
              <span data-testid="build-info" className="md:whitespace-nowrap">
                v{__APP_VERSION__}
              </span>
            </span>
          </p>
        </div>

        {/* Right: divider-separated links. */}
        <nav aria-label="Footer" className="flex shrink-0 items-center">
          {LINKS.map(({ label, to, href }, i) => (
            <span key={label} className="flex items-center">
              {i > 0 && <span aria-hidden="true" className={DIVIDER_CLASS} />}
              {to ? (
                <Link to={to} className={LINK_CLASS}>
                  {label}
                </Link>
              ) : href ? (
                <a href={href} {...EXTERNAL_LINK_PROPS} className={LINK_CLASS}>
                  {label}
                </a>
              ) : (
                <button type="button" className={LINK_CLASS}>
                  {label}
                </button>
              )}
            </span>
          ))}
          {contactSupport && (
            <>
              <span aria-hidden="true" className={DIVIDER_CLASS} />
              <button
                type="button"
                onClick={chatEnabled ? openChat : undefined}
                className="inline-flex items-center gap-1.5 font-medium text-brk-accent transition-colors hover:text-brk-accent-strong"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4 13a8 8 0 0 1 16 0M4 13v3a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 1Zm16 0v3a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 1Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Contact Support
              </button>
            </>
          )}
        </nav>
      </div>
    </footer>
  );
}
